/**
 * Date formatting utilities for the frontend.
 * Provides consistent date/time formatting across the application.
 * 
 * Format: dd-MM-yyyy (Indian/European format)
 * Timezone: User's local timezone (browser default) or selectable
 */

/**
 * Supported timezone options for the application
 */
export const TIMEZONES = [
  { value: 'Asia/Kolkata', label: 'IST (India)', offset: '+05:30' },
  { value: 'America/New_York', label: 'EST (New York)', offset: '-05:00' },
  { value: 'America/Los_Angeles', label: 'PST (Los Angeles)', offset: '-08:00' },
  { value: 'Europe/London', label: 'GMT (London)', offset: '+00:00' },
  { value: 'Europe/Paris', label: 'CET (Paris)', offset: '+01:00' },
  { value: 'Asia/Dubai', label: 'GST (Dubai)', offset: '+04:00' },
  { value: 'Asia/Singapore', label: 'SGT (Singapore)', offset: '+08:00' },
  { value: 'Asia/Tokyo', label: 'JST (Tokyo)', offset: '+09:00' },
  { value: 'Australia/Sydney', label: 'AEDT (Sydney)', offset: '+11:00' },
];

/**
 * Get user's preferred timezone from localStorage or default to browser timezone
 */
export function getUserTimezone(): string {
  const stored = localStorage.getItem('userTimezone');
  if (stored) return stored;
  
  // Default to browser timezone
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata';
}

/**
 * Set user's preferred timezone in localStorage
 */
export function setUserTimezone(timezone: string): void {
  localStorage.setItem('userTimezone', timezone);
}

/**
 * Format date as dd-MM-yyyy
 * @param date Date string, Date object, or timestamp
 * @param timezone Optional timezone (defaults to user's preference)
 */
export function formatDate(
  date: string | Date | number | null | undefined,
  timezone?: string
): string {
  if (!date) return 'N/A';
  
  try {
    const dateObj = typeof date === 'string' || typeof date === 'number' 
      ? new Date(date) 
      : date;
    
    if (isNaN(dateObj.getTime())) return 'Invalid Date';
    
    const tz = timezone || getUserTimezone();
    
    const formatter = new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: tz,
    });
    
    return formatter.format(dateObj);
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'N/A';
  }
}

/**
 * Format date and time as dd-MM-yyyy HH:mm:ss
 * @param date Date string, Date object, or timestamp
 * @param timezone Optional timezone (defaults to user's preference)
 */
export function formatDateTime(
  date: string | Date | number | null | undefined,
  timezone?: string
): string {
  if (!date) return 'N/A';
  
  try {
    const dateObj = typeof date === 'string' || typeof date === 'number' 
      ? new Date(date) 
      : date;
    
    if (isNaN(dateObj.getTime())) return 'Invalid Date';
    
    const tz = timezone || getUserTimezone();
    
    const formatter = new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZone: tz,
    });
    
    return formatter.format(dateObj);
  } catch (error) {
    console.error('Error formatting datetime:', error);
    return 'N/A';
  }
}

/**
 * Format date and time as dd-MM-yyyy HH:mm (without seconds)
 * @param date Date string, Date object, or timestamp
 * @param timezone Optional timezone (defaults to user's preference)
 */
export function formatDateTimeShort(
  date: string | Date | number | null | undefined,
  timezone?: string
): string {
  if (!date) return 'N/A';
  
  try {
    const dateObj = typeof date === 'string' || typeof date === 'number' 
      ? new Date(date) 
      : date;
    
    if (isNaN(dateObj.getTime())) return 'Invalid Date';
    
    const tz = timezone || getUserTimezone();
    
    const formatter = new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: tz,
    });
    
    return formatter.format(dateObj);
  } catch (error) {
    console.error('Error formatting datetime:', error);
    return 'N/A';
  }
}

/**
 * Format date with timezone abbreviation
 * Example: "14-01-2026 10:30 IST"
 */
export function formatDateTimeWithTZ(
  date: string | Date | number | null | undefined,
  timezone?: string
): string {
  if (!date) return 'N/A';
  
  try {
    const dateObj = typeof date === 'string' || typeof date === 'number' 
      ? new Date(date) 
      : date;
    
    if (isNaN(dateObj.getTime())) return 'Invalid Date';
    
    const tz = timezone || getUserTimezone();
    
    const formattedDateTime = formatDateTimeShort(dateObj, tz);
    
    // Get timezone abbreviation
    const tzInfo = TIMEZONES.find(t => t.value === tz);
    const tzName = tzInfo?.label.match(/\(([^)]+)\)/)?.[1] || tz;
    
    return `${formattedDateTime} ${tzName}`;
  } catch (error) {
    console.error('Error formatting datetime with TZ:', error);
    return 'N/A';
  }
}

/**
 * Format time only as HH:mm:ss
 */
export function formatTime(
  date: string | Date | number | null | undefined,
  timezone?: string
): string {
  if (!date) return 'N/A';
  
  try {
    const dateObj = typeof date === 'string' || typeof date === 'number' 
      ? new Date(date) 
      : date;
    
    if (isNaN(dateObj.getTime())) return 'Invalid Time';
    
    const tz = timezone || getUserTimezone();
    
    const formatter = new Intl.DateTimeFormat('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZone: tz,
    });
    
    return formatter.format(dateObj);
  } catch (error) {
    console.error('Error formatting time:', error);
    return 'N/A';
  }
}

/**
 * Format relative time (e.g., "2 hours ago", "5 minutes ago")
 */
export function formatRelativeTime(
  date: string | Date | number | null | undefined
): string {
  if (!date) return 'N/A';
  
  try {
    const dateObj = typeof date === 'string' || typeof date === 'number' 
      ? new Date(date) 
      : date;
    
    if (isNaN(dateObj.getTime())) return 'Invalid Date';
    
    const now = new Date();
    const diffMs = now.getTime() - dateObj.getTime();
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffSecs < 60) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 30) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return formatDate(dateObj);
  } catch (error) {
    console.error('Error formatting relative time:', error);
    return 'N/A';
  }
}

/**
 * Parse dd-MM-yyyy string to Date object
 */
export function parseDate(dateString: string): Date | null {
  if (!dateString) return null;
  
  try {
    const parts = dateString.split('-');
    if (parts.length !== 3) return null;
    
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
    const year = parseInt(parts[2], 10);
    
    const date = new Date(year, month, day);
    
    if (isNaN(date.getTime())) return null;
    
    return date;
  } catch (error) {
    console.error('Error parsing date:', error);
    return null;
  }
}

/**
 * Check if a date string is valid
 */
export function isValidDate(date: any): boolean {
  if (!date) return false;
  
  try {
    const dateObj = typeof date === 'string' || typeof date === 'number' 
      ? new Date(date) 
      : date;
    
    return !isNaN(dateObj.getTime());
  } catch {
    return false;
  }
}

/**
 * Get current date in dd-MM-yyyy format
 */
export function getCurrentDate(timezone?: string): string {
  return formatDate(new Date(), timezone);
}

/**
 * Get current datetime in dd-MM-yyyy HH:mm:ss format
 */
export function getCurrentDateTime(timezone?: string): string {
  return formatDateTime(new Date(), timezone);
}
